import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { postsService, CreatePostData } from '../../services/posts.service';
import { usersService } from '../../services/users.service';
import { useAuth } from '../../context/AuthContext';
import { mergeBranchOptions } from '../../constants/branches';
import {
  DEPARTMENT_ONLY_OPTIONS,
  matchDepartmentOnlyOption,
} from '../../constants/departmentOptions';
import { isElevatedFeedRole, normalizeRole } from '../../utils/roles';
import Combobox from '../Combobox';
import './CreatePostModal.css';

const MAX_MEDIA_BYTES = 100 * 1024 * 1024;

interface CreatePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPostCreated: () => void;
}

const CreatePostModal: React.FC<CreatePostModalProps> = ({
  isOpen,
  onClose,
  onPostCreated,
}) => {
  const { user } = useAuth();
  const [caption, setCaption] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<'POST' | 'ANNOUNCEMENT' | 'POLICY'>('POST');
  const [accessScope, setAccessScope] = useState<'COMPANY' | 'BRANCH' | 'DEPARTMENT_ONLY'>('COMPANY');
  const [branch, setBranch] = useState('');
  const [targetDepartment, setTargetDepartment] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [shouldNotify, setShouldNotify] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [branchOptions, setBranchOptions] = useState<string[]>(() => mergeBranchOptions([]));
  const [departmentOptions, setDepartmentOptions] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prevAccessScopeRef = useRef(accessScope);

  const r = normalizeRole(user?.role);
  const isBranchAdmin = r === 'BRANCH_ADMIN';
  const isDeptAdmin = r === 'DEPARTMENT_ADMIN';
  const needsElevatedScope = isElevatedFeedRole(user?.role) && !isBranchAdmin && !isDeptAdmin;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && user) {
      // Always reset on open so the dropdown/value matches the available options.
      setAccessScope('COMPANY');
      prevAccessScopeRef.current = 'COMPANY';
      setBranch(user.branch || '');
      setTargetDepartment(user.department || '');
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (!isOpen || !needsElevatedScope) return;
    let cancelled = false;
    usersService
      .getBranches()
      .then((list) => {
        if (!cancelled) setBranchOptions(mergeBranchOptions(list));
      })
      .catch(() => {
        if (!cancelled) setBranchOptions(mergeBranchOptions([]));
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, needsElevatedScope]);

  useEffect(() => {
    if (!needsElevatedScope) {
      setDepartmentOptions([]);
      return;
    }

    // Branch-only posts: no departments dropdown needed.
    if (accessScope !== 'DEPARTMENT_ONLY') {
      setDepartmentOptions([]);
      return;
    }

    setDepartmentOptions(Array.from(DEPARTMENT_ONLY_OPTIONS));
  }, [needsElevatedScope, accessScope]);

  useEffect(() => {
    if (!isOpen) return;
    if (accessScope !== 'DEPARTMENT_ONLY') return;
    // If the logged-in user's department isn't one of the business-visible departments,
    // don't preselect anything.
    if (targetDepartment) {
      const normalized = targetDepartment.trim().toUpperCase();
      const match = DEPARTMENT_ONLY_OPTIONS.find((d) => d.toUpperCase() === normalized);
      if (match && match !== targetDepartment) {
        setTargetDepartment(match);
      } else if (!match) {
        setTargetDepartment('');
      }
    }
  }, [accessScope, isOpen, targetDepartment]);

  // When user switches Access to "one department only", default Department from their profile
  // (e.g. logged-in user is HR → preselect HR) when it matches the allowed list.
  useEffect(() => {
    if (!isOpen || !needsElevatedScope) return;
    const entered =
      accessScope === 'DEPARTMENT_ONLY' && prevAccessScopeRef.current !== 'DEPARTMENT_ONLY';
    prevAccessScopeRef.current = accessScope;
    if (!entered) return;
    const fromProfile = matchDepartmentOnlyOption(user?.department);
    if (fromProfile) setTargetDepartment(fromProfile);
  }, [isOpen, needsElevatedScope, accessScope, user?.department]);

  useEffect(() => {
    return () => {
      if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  useEffect(() => {
    if (!isOpen) {
      setImage(null);
      setImagePreview(null);
      setError('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const revokePreview = (url: string | null) => {
    if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    revokePreview(imagePreview);
    if (file) {
      if (file.size > MAX_MEDIA_BYTES) {
        setError(`File must be under ${MAX_MEDIA_BYTES / (1024 * 1024)} MB`);
        setImage(null);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      setError('');
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImage(null);
      setImagePreview(null);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = () => {
    revokePreview(imagePreview);
    setImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!caption.trim()) {
      setError('Caption is required');
      return;
    }

    setIsSubmitting(true);

    try {
      let cdnLink = '';

      if (image) {
        const imageFormData = new FormData();
        imageFormData.append('file', image);
        const uploadResponse = await postsService.uploadImage(imageFormData);
        cdnLink = uploadResponse.url;
      }

      const base: CreatePostData = {
        caption: caption.trim(),
        content: content.trim() || undefined,
        category,
        cdnLink: cdnLink || undefined,
        shouldNotify,
      };

      if (needsElevatedScope) {
        if (accessScope === 'COMPANY') {
          base.accessScope = 'COMPANY';
        } else if (accessScope === 'BRANCH') {
          if (!branch.trim()) {
            setError('Branch is required');
            setIsSubmitting(false);
            return;
          }
          base.accessScope = 'BRANCH';
          base.branch = branch.trim();
        } else if (accessScope === 'DEPARTMENT_ONLY') {
          if (!targetDepartment.trim()) {
            setError('Target department is required');
            setIsSubmitting(false);
            return;
          }
          // Visible to the chosen department in every branch. No branch scoping.
          base.accessScope = 'DEPARTMENT_ONLY';
          base.department = targetDepartment.trim();
        }
      }

      await postsService.createPost(base);

      setCaption('');
      setContent('');
      setCategory('POST');
      setAccessScope('COMPANY');
      setImage(null);
      setImagePreview(null);
      setShouldNotify(false);

      onPostCreated();
      onClose();
    } catch (err: any) {
      const msg = err.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(' ') : msg || 'Failed to create post');
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className="modal-overlay">
      <div className="modal-content post-form-modal">
        <div className="modal-header">
          <h2>Create Post</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <form className="post-form-modal__form" onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-message">{error}</div>}

            <div className="form-group">
              <label htmlFor="caption">Title *</label>
              <input
                type="text"
                id="caption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Enter post title..."
                maxLength={200}
              />
            </div>

            <div className="form-group">
              <label htmlFor="content">Content</label>
              <textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your post content..."
                rows={4}
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">Category</label>
              <Combobox
                id="category"
                ariaLabel="Category"
                value={category}
                onChange={(v) => setCategory(v as 'POST' | 'ANNOUNCEMENT' | 'POLICY')}
                options={[
                  { value: 'POST', label: 'Post' },
                  { value: 'ANNOUNCEMENT', label: 'Announcement' },
                  { value: 'POLICY', label: 'Policy' },
                ]}
              />
            </div>

            {isBranchAdmin && (
              <div className="form-group">
                <p className="field-note">This post will be branch-wide for <strong>{user?.branch}</strong> only.</p>
              </div>
            )}

            {isDeptAdmin && (
              <div className="form-group">
                <p className="field-note">
                  This post will be visible only to <strong>{user?.department}</strong>.
                </p>
              </div>
            )}

            {needsElevatedScope && (
              <>
                <div className="form-group">
                  <label htmlFor="access-scope">Access</label>
                  <Combobox
                    id="access-scope"
                    ariaLabel="Access scope"
                    value={accessScope}
                    onChange={(v) =>
                      setAccessScope(v as 'COMPANY' | 'BRANCH' | 'DEPARTMENT_ONLY')
                    }
                    options={[
                      { value: 'COMPANY', label: 'Home feed (all users)', hint: 'Company-wide' },
                      { value: 'BRANCH', label: 'One branch only', hint: 'Branch feed' },
                      { value: 'DEPARTMENT_ONLY', label: 'One department only', hint: 'Dept feed' },
                    ]}
                  />
                  {accessScope === 'COMPANY' && (
                    <p className="field-note">
                      Visible on the main Home feed for every user in the company.
                    </p>
                  )}
                </div>
                {accessScope === 'BRANCH' && (
                <div className="form-group">
                  <label htmlFor="target-branch">Branch *</label>
                  <Combobox
                    id="target-branch"
                    ariaLabel="Branch"
                    value={branch}
                    onChange={(v) => {
                      setBranch(v);
                      setTargetDepartment('');
                    }}
                    required
                    placeholder="Select branch…"
                    options={branchOptions.map((b) => ({ value: b, label: b }))}
                  />
                </div>
                )}
                {accessScope === 'DEPARTMENT_ONLY' && (
                  <div className="form-group">
                    <label htmlFor="target-dept">Department *</label>
                    <Combobox
                      id="target-dept"
                      ariaLabel="Department"
                      value={targetDepartment}
                      onChange={(v) => setTargetDepartment(v)}
                      required
                      placeholder="Select department…"
                      options={departmentOptions.map((d) => ({ value: d, label: d }))}
                    />
                  </div>
                )}
              </>
            )}

            <div className="form-group">
              <label>Image or video (optional)</label>
              <div className="image-upload-area">
                {imagePreview ? (
                  <div className="image-preview">
                    {image?.type.startsWith('video/') ? (
                      <video src={imagePreview} className="preview-media" controls muted playsInline />
                    ) : (
                      <img src={imagePreview} alt="Preview" />
                    )}
                    <button
                      type="button"
                      className="remove-image"
                      onClick={removeImage}
                    >
                      &times;
                    </button>
                  </div>
                ) : (
                  <div
                    className="upload-placeholder"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <span className="upload-icon">📷</span>
                    <span>Click to upload image or video</span>
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  accept="image/*,video/*"
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={shouldNotify}
                  onChange={(e) => setShouldNotify(e.target.checked)}
                />
                <span>Send notification to users who can see this post</span>
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Post'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CreatePostModal;
